import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useMyProducers } from '@/hooks/useMyProducers';

export type PortalMode = 'admin' | 'producer' | 'customer' | 'basic';

interface UsePortalModeResult {
  mode: PortalMode;
  isLoading: boolean;
  error: Error | null;
  hasBookings: boolean;
  hasOrders: boolean;
  bookingsCount: number;
  ordersCount: number;
}

/**
 * Determines the portal mode for the current user:
 * - admin: if user has admin role
 * - producer: if user has producer role or has producers linked
 * - customer: if user has claimed bookings or orders (user_id set)
 * - basic: otherwise
 */
export function usePortalMode(): UsePortalModeResult {
  const { user, isAdmin, isProducer, isLoading: authLoading, rolesLoading } = useAuth();
  const { hasProducers, isLoading: producersLoading } = useMyProducers();

  // Query to count user's bookings and orders
  const { data: counts, isLoading: countsLoading, error } = useQuery({
    queryKey: ['portal-mode-counts', user?.id],
    queryFn: async () => {
      if (!user) return { bookings: 0, orders: 0 };

      // Count event bookings where user_id matches
      const { count: eventBookingsCount, error: bookingsError } = await supabase
        .from('event_bookings')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (bookingsError) {
        console.error('Error counting bookings:', bookingsError);
      }

      // Count accommodation bookings by guest email
      const { count: accBookingsCount, error: accError } = await supabase
        .from('accommodation_bookings')
        .select('*', { count: 'exact', head: true })
        .eq('guest_email_norm', user.email?.toLowerCase() ?? '');

      if (accError) {
        console.error('Error counting accommodation bookings:', accError);
      }

      // Count orders where user_id matches (from orders table)
      const { count: ordersCount, error: ordersError } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (ordersError) {
        console.error('Error counting orders:', ordersError);
      }

      return {
        bookings: (eventBookingsCount ?? 0) + (accBookingsCount ?? 0),
        orders: ordersCount ?? 0,
      };
    },
    enabled: !!user && !authLoading && !rolesLoading,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const isLoading = authLoading || rolesLoading || producersLoading || countsLoading;

  const bookingsCount = counts?.bookings ?? 0;
  const ordersCount = counts?.orders ?? 0;
  const hasBookings = bookingsCount > 0;
  const hasOrders = ordersCount > 0;
  const hasCustomerData = hasBookings || hasOrders;

  // Determine mode
  let mode: PortalMode = 'basic';
  
  if (!isLoading) {
    if (isAdmin) {
      mode = 'admin';
    } else if (isProducer || hasProducers) {
      mode = 'producer';
    } else if (hasCustomerData) {
      mode = 'customer';
    }
  }

  return {
    mode,
    isLoading,
    error: error as Error | null,
    hasBookings,
    hasOrders,
    bookingsCount,
    ordersCount,
  };
}
